import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Footer from "./../Footer";
import Header from "./../Header";
import { assetUrl } from "../../utils/api";

export default function ConsultantChat() {
  const params = useParams();
  const consultantId = params.consultantId || params.id;
  const location = useLocation();
  const navigate = useNavigate();
  const bottomRef = useRef(null);
    const prevMessageCountRef = useRef(0);


  const userId = localStorage.getItem("user_id");
  const [guest] = useState(() => location.state?.guest || getSavedGuest());

  const [consultant, setConsultant] = useState(null);
  const [messages, setMessages] = useState([]);
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(true);

  const visitorType = userId ? "user" : "guest";
  const canChat = Boolean(userId || guest);

  useEffect(() => {
    if (!canChat) {
      alert("请先填写咨询资料");
      navigate(`/consultants2/${consultantId}`);
    }
  }, [canChat, consultantId, navigate]);

  useEffect(() => {
    if (!canChat || !consultantId) {
      return;
    }

    fetchConsultant();
    fetchMessages();

    const timer = setInterval(() => {
      fetchMessages();
    }, 6000);

    return () => clearInterval(timer);
  }, [canChat, consultantId]);

 useEffect(() => {
  if (messages.length > prevMessageCountRef.current) {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }

  prevMessageCountRef.current = messages.length;
}, [messages]);


  const fetchConsultant = async () => {
    try {
      const res = await fetch(
        `/api/consultant/profile/${consultantId}`
      );
      const data = await res.json();
      setConsultant(data || null);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async () => {
    try {
      const query = new URLSearchParams({
        consultant_id: String(consultantId),
        sender_type: visitorType,
      });

      if (userId) {
        query.set("user_id", userId);
      } else {
        query.set("guest_id", guest.id);
      }

      const res = await fetch(
        `/api/consultant/messages?${query.toString()}`
      );
      const data = await res.json();
      setMessages(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
    }
  };

  const sendMessage = async () => {
    const text = content.trim();

    if (!text) {
      return;
    }

    if (!canChat) {
      alert("请先填写咨询资料");
      navigate(`/consultants2/${consultantId}`);
      return;
    }

    try {
      const payload = {
        consultant_id: Number(consultantId),
        sender_type: visitorType,
        content: text,
      };

      if (userId) {
        payload.user_id = Number(userId);
      } else {
        payload.guest_id = guest.id;
        payload.guest_name = guest.name;
        payload.guest_contact = guest.contact || "";
      }

      const res = await fetch("/api/consultant/messages", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (res.ok) {
        setContent("");
        fetchMessages();
      } else {
        alert(getErrorMessage(data, "发送失败"));
      }
    } catch (err) {
      console.error(err);
      alert("发送失败");
    }
  };

  if (!canChat) {
    return null;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-5xl mx-auto px-4 py-10">加载中...</div>
        <Footer />
      </div>
    );
  }

  if (!consultant) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-5xl mx-auto px-4 py-10">咨询师不存在</div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />

      <main className="flex-1 max-w-5xl mx-auto w-full px-4 py-8">
        <div className="bg-white rounded-2xl shadow overflow-hidden">
          <div className="border-b p-5 flex items-center gap-4">
            <img
              src={assetUrl(consultant.avatar) || "/default-avatar.png"}
              alt={consultant.name}
              className="w-14 h-14 rounded-full object-cover bg-gray-100"
            />

            <div className="flex-1">
              <h1 className="text-xl font-bold">{consultant.name}</h1>
              <p className="text-gray-500 text-sm">
                {consultant.specialties || "咨询师"} · ¥
                {consultant.price || 0} / {consultant.duration || 50}分钟
              </p>
            </div>

            {!userId ? (
              <div className="text-right text-sm text-gray-500">
                <p>游客咨询</p>
                <p>{guest.name}</p>
              </div>
            ) : null}
          </div>

          <div className="h-[560px] overflow-y-auto p-5 bg-gray-50 space-y-4">
            {messages.length === 0 ? (
              <div className="text-center text-gray-400 mt-20">
                你可以开始向咨询师提问
              </div>
            ) : (
              messages.map((msg) => {
                const isMe = msg.sender_type === visitorType;

                return (
                  <div
                    key={msg.id}
                    className={`flex ${
                      isMe ? "justify-end" : "justify-start"
                    }`}
                  >
                    <div
                      className={`max-w-[70%] rounded-2xl px-4 py-3 ${
                        isMe
                          ? "bg-blue-500 text-white"
                          : "bg-white text-gray-700 border"
                      }`}
                    >
                      <p className="whitespace-pre-wrap">{msg.content}</p>
                      <p
                        className={`text-xs mt-2 ${
                          isMe ? "text-blue-100" : "text-gray-400"
                        }`}
                      >
                        {msg.created_at
                          ? new Date(msg.created_at).toLocaleString()
                          : ""}
                      </p>
                    </div>
                  </div>
                );
              })
            )}

            <div ref={bottomRef} />
          </div>

          <div className="border-t p-4 flex gap-3">
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="请输入你想咨询的问题..."
              rows={2}
              className="flex-1 border rounded-xl px-4 py-3 resize-none outline-none focus:ring-2 focus:ring-blue-300"
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage();
                }
              }}
            />

            <button
              onClick={sendMessage}
              className="px-6 py-3 rounded-xl bg-blue-500 hover:bg-blue-600 text-white font-bold"
            >
              发送
            </button>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

function getSavedGuest() {
  try {
    const raw = localStorage.getItem("guest_consult_user");
    return raw ? JSON.parse(raw) : null;
  } catch {
    localStorage.removeItem("guest_consult_user");
    return null;
  }
}

function getErrorMessage(data, fallback) {
  if (!data) {
    return fallback;
  }

  if (typeof data.detail === "string") {
    return data.detail;
  }

  if (Array.isArray(data.detail)) {
    return data.detail
      .map((item) => item.msg || item.message || JSON.stringify(item))
      .join("\n");
  }

  if (typeof data.detail === "object") {
    return data.detail.message || data.detail.msg || JSON.stringify(data.detail);
  }

  return fallback;
}
